import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, X, Sparkles, Bot, Loader2 } from "lucide-react";
import { generateTalosResponse } from "@/lib/talosBrain";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const GREETING = {
  id: "greeting",
  sender: "talos",
  text: "Olá! Sou o Talos — a inteligência artificial do MUFT ERP. Como posso ajudar sua empresa hoje?",
};

const SUGGESTIONS = [
  "Quais são os planos e preços?",
  "Há limite de usuários ou equipe?",
  "Como funciona o cadastro por foto?",
  "O ponto digital tem biometria facial?",
];

const renderFormattedText = (text) => {
  if (!text) return null;
  const lines = text.split("\n");
  return lines.map((line, li) => {
    // Process markdown bold **text**
    const parts = line.split(/(\*\*.*?\*\*)/g);
    return (
      <span key={li} className="block">
        {parts.map((p, pi) => {
          if (p.startsWith("**") && p.endsWith("**")) {
            return (
              <strong key={pi} className="font-extrabold text-navy-900 dark:text-white">
                {p.slice(2, -2)}
              </strong>
            );
          }
          if (p.startsWith("*") && p.endsWith("*")) {
            return (
              <em key={pi} className="italic text-lavender">
                {p.slice(1, -1)}
              </em>
            );
          }
          return p;
        })}
      </span>
    );
  });
};

export const TalosChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [sessionId] = useState(() => `session-${Math.random().toString(36).substring(2, 11)}`);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  const streamLocalReply = async (botMsgId, fullText) => {
    const words = fullText.split(" ");
    let accumulated = "";
    for (let i = 0; i < words.length; i++) {
      accumulated += (i > 0 ? " " : "") + words[i];
      setMessages((prev) =>
        prev.map((m) => (m.id === botMsgId ? { ...m, text: accumulated } : m))
      );
      await new Promise((r) => setTimeout(r, 22));
    }
  };

  const sendMessage = async (textToSend) => {
    const text = (textToSend || input).trim();
    if (!text || isStreaming) return;

    setInput("");
    const userMsg = { id: `user-${Date.now()}`, sender: "user", text };
    setMessages((prev) => [...prev, userMsg]);
    setIsStreaming(true);

    const botMsgId = `talos-${Date.now()}`;
    setMessages((prev) => [...prev, { id: botMsgId, sender: "talos", text: "" }]);

    let successStreaming = false;

    // Try backend streaming if API url is present
    if (API && API !== "/api") {
      try {
        const response = await fetch(`${API}/talos/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: sessionId,
            message: text,
          }),
        });

        if (response.ok && response.body) {
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let accumulated = "";

          while (true) {
            const { value, done } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split("\n\n");

            for (const line of lines) {
              if (line.startsWith("data: ")) {
                try {
                  const data = JSON.parse(line.replace("data: ", ""));
                  if (data.delta) {
                    accumulated += data.delta;
                    setMessages((prev) =>
                      prev.map((m) => (m.id === botMsgId ? { ...m, text: accumulated } : m))
                    );
                    successStreaming = true;
                  }
                } catch (e) {
                  // ignore
                }
              }
            }
          }
        }
      } catch (err) {
        successStreaming = false;
      }
    }

    // High-performance intelligent engine fallback
    if (!successStreaming) {
      const expertResponse = generateTalosResponse(text);
      await streamLocalReply(botMsgId, expertResponse);
    }

    setIsStreaming(false);
  };

  return (
    <>
      {/* Floating Launcher Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <AnimatePresence>
          {!isOpen && (
            <motion.button
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              whileHover={{ scale: 1.06 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsOpen(true)}
              data-testid="talos-chat-trigger"
              className="flex items-center gap-2.5 rounded-full bg-navy-800 px-5 py-3.5 text-white shadow-2xl border-2 border-lavender/40 hover:bg-navy-700 transition-colors"
            >
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-lavender text-navy-900 shadow-sm">
                <Bot className="h-4 w-4" />
              </div>
              <div className="text-left">
                <p className="text-xs font-black leading-none">Falar com Talos</p>
                <p className="text-[10px] text-lavender font-medium mt-0.5">IA Especialista MUFT</p>
              </div>
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {/* Floating Chat Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="fixed bottom-6 right-6 z-50 flex h-[580px] w-[92vw] max-w-[420px] flex-col overflow-hidden rounded-3xl border border-navy-100 bg-white shadow-2xl dark:border-white/10 dark:bg-navy-900"
            data-testid="talos-chat-window"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-navy-50 bg-navy-800 px-5 py-4 text-white dark:border-white/10">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-lavender text-navy-900 shadow-sm">
                  <Bot className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-black">Talos</p>
                    <span className="rounded-full bg-lavender/30 px-1.5 py-0.5 text-[9px] font-bold text-lavender uppercase tracking-wider">
                      IA Especialista MUFT
                    </span>
                  </div>
                  <p className="text-[10px] text-emerald-400 font-semibold flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> Online agora
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="rounded-full p-1.5 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                aria-label="Fechar chat"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3.5 bg-ice/50 dark:bg-navy-950/40">
              {messages.map((m) => {
                const isUser = m.sender === "user";
                return (
                  <div
                    key={m.id}
                    className={`flex items-start gap-2 ${isUser ? "justify-end" : "justify-start"}`}
                  >
                    {!isUser && (
                      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-navy-800 text-lavender mt-0.5 shadow-sm">
                        <Sparkles className="h-3 w-3" />
                      </div>
                    )}
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-xs font-medium leading-relaxed shadow-sm ${
                        isUser
                          ? "rounded-br-sm bg-navy-800 text-white"
                          : "rounded-bl-sm border border-navy-100 bg-white text-navy-800 dark:border-white/10 dark:bg-navy-800 dark:text-white"
                      }`}
                    >
                      {m.text ? (
                        renderFormattedText(m.text)
                      ) : (
                        <span className="inline-flex items-center gap-1 text-navy-400">
                          <Loader2 className="h-3 w-3 animate-spin" /> Talos está digitando...
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Suggestions */}
            {messages.length <= 2 && (
              <div className="px-4 py-2.5 border-t border-navy-50 bg-white dark:border-white/5 dark:bg-navy-900">
                <p className="text-[10px] font-bold uppercase tracking-wider text-navy-400 mb-1.5">
                  Sugestões de perguntas:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(s)}
                      className="rounded-full border border-navy-100 bg-ice px-3 py-1 text-[11px] font-semibold text-navy-700 hover:border-navy-200 hover:bg-white dark:border-white/10 dark:bg-navy-800 dark:text-white transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                sendMessage();
              }}
              className="flex items-center gap-2 border-t border-navy-100 bg-white p-3 dark:border-white/10 dark:bg-navy-900"
            >
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Pergunte sobre planos, fiscal, ponto, equipe..."
                disabled={isStreaming}
                className="flex-1 rounded-full border border-navy-200 bg-ice px-4 py-2 text-xs font-medium text-navy-800 placeholder-navy-400 focus:border-navy-800 focus:outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
              />
              <button
                type="submit"
                disabled={!input.trim() || isStreaming}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-navy-800 text-white shadow-sm transition-all hover:bg-navy-700 disabled:opacity-50"
                aria-label="Enviar mensagem"
              >
                {isStreaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default TalosChat;
